import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

// ---------- Plan de cuentas ----------

export type AccountType = "ACTIVO" | "PASIVO" | "PATRIMONIO" | "INGRESO" | "GASTO" | "COSTO";

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  ACTIVO: "Activo",
  PASIVO: "Pasivo",
  PATRIMONIO: "Patrimonio",
  INGRESO: "Ingreso",
  GASTO: "Gasto",
  COSTO: "Costo",
};

export interface ChartOfAccountRecord {
  id: string;
  code: string;
  name: string;
  accountType: AccountType;
  parentId: string | null;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  parent?: { id: string; code: string; name: string } | null;
}

export interface ListChartOfAccountsFilters {
  page?: number;
  pageSize?: number;
  accountType?: AccountType;
  parentId?: string;
  search?: string;
}

export function useChartOfAccounts(filters: ListChartOfAccountsFilters = {}) {
  return useQuery({
    queryKey: ["chart-of-accounts", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ChartOfAccountRecord[]>>("/accounting/accounts", { params: filters });
      return data;
    },
  });
}

export interface CreateChartOfAccountPayload {
  code: string;
  name: string;
  accountType: AccountType;
  parentId?: string;
  description?: string;
  isActive?: boolean;
}

export function useCreateChartOfAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateChartOfAccountPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ChartOfAccountRecord>>("/accounting/accounts", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["chart-of-accounts"] }),
  });
}

export function useUpdateChartOfAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CreateChartOfAccountPayload> & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<ChartOfAccountRecord>>(`/accounting/accounts/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["chart-of-accounts"] }),
  });
}

export function useDeleteChartOfAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/accounting/accounts/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["chart-of-accounts"] }),
  });
}

// ---------- Asientos contables ----------

export type JournalEntryStatus = "BORRADOR" | "CONTABILIZADO" | "ANULADO";

export const JOURNAL_ENTRY_STATUS_LABEL: Record<JournalEntryStatus, string> = {
  BORRADOR: "Borrador",
  CONTABILIZADO: "Contabilizado",
  ANULADO: "Anulado",
};

export interface JournalEntryLineRecord {
  id: string;
  chartOfAccountId: string;
  debit: string;
  credit: string;
  description: string | null;
  account?: { id: string; code: string; name: string; accountType: AccountType } | null;
}

export interface JournalEntryRecord {
  id: string;
  clientId: string | null;
  entryNumber: number;
  entryDate: string;
  description: string;
  status: JournalEntryStatus;
  postedAt: string | null;
  voidedAt: string | null;
  createdAt: string;
  lines: JournalEntryLineRecord[];
  client?: { id: string; fullName: string | null; businessName: string | null; tradeName: string | null; clientType: string } | null;
  createdBy?: { id: string; fullName: string; email: string } | null;
}

export interface ListJournalEntriesFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: JournalEntryStatus;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export function useJournalEntries(filters: ListJournalEntriesFilters = {}) {
  return useQuery({
    queryKey: ["journal-entries", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<JournalEntryRecord[]>>("/accounting/entries", { params: filters });
      return data;
    },
  });
}

export interface JournalEntryLinePayload {
  chartOfAccountId: string;
  debit?: number | string;
  credit?: number | string;
  description?: string;
}

export interface CreateJournalEntryPayload {
  clientId?: string;
  entryDate: string;
  description: string;
  lines: JournalEntryLinePayload[];
}

export function useCreateJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateJournalEntryPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<JournalEntryRecord>>("/accounting/entries", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["journal-entries"] }),
  });
}

export function usePostJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<JournalEntryRecord>>(`/accounting/entries/${id}/post`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["journal-entries"] }),
  });
}

export function useVoidJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<JournalEntryRecord>>(`/accounting/entries/${id}/void`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["journal-entries"] }),
  });
}

export function useDeleteJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/accounting/entries/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["journal-entries"] }),
  });
}

export function journalEntryClientName(entry: JournalEntryRecord): string | null {
  if (!entry.client) return null;
  return entry.client.clientType === "PERSONA_JURIDICA"
    ? entry.client.businessName || entry.client.tradeName || "Cliente"
    : entry.client.fullName || "Cliente";
}

export function journalEntryTotal(entry: JournalEntryRecord): number {
  return entry.lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
}
